import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Switch from "@mui/material/Switch";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Button, TextField, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  deletePoster,
  getGamesData,
  getGame,
  saveGameSettings,
  getLinks,
  loadPoster,
  getProfile,
} from "../redux/asyncThunk/userActions";
import InputFileUpload from "./UploadFile";
import { formatDateTimeRu } from "../utils/formatDate";
import { sanitizePlainText } from "../utils/inputValidation";
import {
  EXAMPLE_START_POST,
  getProjectStatusFromSettings,
  settingsFromProjectStatus,
  mergeGameSettingsWithGamification,
} from "../utils/projectAccess";
import { readGamificationFromSettings } from "../utils/gamification";
import GamificationSettings from "./GamificationSettings";
import { readChatAccessGateFromSettings, DEFAULT_CHAT_ACCESS_GATE } from "../utils/chatAccessGate";
import { deriveLegacyMessenger } from "../utils/messengers";
import { getContentTypeLabel, resolveGameContentType } from "../utils/contentType";
import ProjectTeamAccess from "./ProjectTeamAccess";
import { readAvailableMessengersFromSettings } from "./ProjectMessengerAvailability";
import { userSlice } from "../redux/user/userSlice";
import { AUTH_FIELD_SX } from "../pages/AuthLayout";
import { getServerMediaUrl } from "../utils/mediaUrl";
import { SERVICE_LOGO_SRC } from "../utils/startPostPreview";
import "./index.css";

const { addMessage } = userSlice;

const fieldSx = { ...AUTH_FIELD_SX, maxWidth: "100%", width: "100%" };

const DESCRIPTION_PLACEHOLDER =
  "Этот сценарий поможет освоить вам ремонт кофемашины Макита. Вы изучите как починить и не сломать.";

const SETTINGS_STEPS = [
  {
    id: "start",
    label: "Стартовый пост",
    hint: "Что увидит сотрудник при открытии ссылки",
  },
  {
    id: "gamification",
    label: "Мотивация",
    hint: "XP, рейтинг и достижения — по желанию",
  },
  {
    id: "access",
    label: "Доступ",
    hint: "Ссылки для Telegram и MAX — отправьте их сотрудникам",
  },
  {
    id: "publish",
    label: "Публикация",
    hint: "",
  },
];

export default function Settings() {
  const dispatch = useDispatch();
  const game = useSelector((state) => state.user.game);
  const [data, setData] = useState();
  const loadedGameIdRef = useRef(null);
  const [activeStep, setActiveStep] = useState("start");
  const [saveState, setSaveState] = useState("idle");
  const [chatAccessGate, setChatAccessGate] = useState(DEFAULT_CHAT_ACCESS_GATE);
  const [settings, setSettings] = useState({
    title: "",
    category: "",
    program: "",
    description: "",
    private_access: false,
    allow_replay: true,
    availableMessengers: ["telegram", "max"],
    projectStatus: "draft",
    gamification: readGamificationFromSettings(game?.settings),
  });

  const user = useSelector((state) => state.user?.data?.user);
  const profile = useSelector((state) => state.user?.profile);

  useEffect(() => {
    if (user?.id && !profile?.login) {
      dispatch(getProfile({ id: user.id }));
    }
  }, [dispatch, user?.id, profile?.login]);

  useEffect(() => {
    if (!game) return;

    setData({
      ...game,
      scenesValue: game?.nodes?.filter((el) => el.type === "group")?.length,
    });

    if (!game._id || loadedGameIdRef.current === game._id) return;
    loadedGameIdRef.current = game._id;
    setSettings({
      title: game.title ?? "",
      category: game.category ?? "",
      program: game.program ?? "",
      description: game.description ?? "",
      private_access: false,
      allow_replay: game.settings?.allow_replay !== false,
      availableMessengers: readAvailableMessengersFromSettings(game.settings),
      projectStatus: getProjectStatusFromSettings(game.settings),
      gamification: readGamificationFromSettings(game.settings),
    });
    setChatAccessGate(readChatAccessGateFromSettings(game.settings));
  }, [game]);

  const handleChange = (event, property) => {
    setSettings((prev) => ({
      ...prev,
      [property]:
        typeof event.target.value === "string"
          ? sanitizePlainText(
              event.target.value,
              property === "description" ? 2000 : 200
            )
          : event.target.value,
    }));
  };

  const handleToggle = (property) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [property]: event.target.checked,
    }));
  };

  const buildSettingsPayload = (saveProjectStatus = true) => {
    const statusFlags = settingsFromProjectStatus(
      saveProjectStatus
        ? settings.projectStatus
        : getProjectStatusFromSettings(game?.settings),
      game?.settings
    );

    return mergeGameSettingsWithGamification(game?.settings, {
      private_access: !statusFlags.published,
      published: statusFlags.published,
      hidden: statusFlags.hidden,
      completion_text: game?.settings?.completion_text ?? "",
      author_telegram: game?.settings?.author_telegram ?? "",
      allow_replay: settings.allow_replay,
      availableMessengers: settings.availableMessengers,
      messenger: deriveLegacyMessenger(settings.availableMessengers),
      contentType: resolveGameContentType(game),
      gamification: settings.gamification,
      ...chatAccessGate,
    });
  };

  const handleUpdateSettings = async ({
    showSaveError = false,
    saveProjectStatus = false,
  } = {}) => {
    setSaveState("saving");

    try {
      await dispatch(
        saveGameSettings({
          game: game._id,
          title: settings.title,
          description: settings.description,
          category: settings.category,
          program: settings.program,
          settings: buildSettingsPayload(saveProjectStatus),
          silent: true,
          showSaveError,
          publishAttempt: showSaveError,
          onSucces: () => {
            dispatch(getLinks({ userId: user.id }));
          },
        })
      ).unwrap();
      setSaveState("saved");
      window.setTimeout(() => setSaveState("idle"), 2500);
      return true;
    } catch {
      setSaveState("idle");
      return false;
    }
  };

  const handleNextStep = async () => {
    const currentStepIndex = SETTINGS_STEPS.findIndex(
      (step) => step.id === activeStep
    );
    const isFinalStep = currentStepIndex === SETTINGS_STEPS.length - 1;
    const reportPublishError =
      isFinalStep && settings.projectStatus === "published";
    const saved = await handleUpdateSettings({
      showSaveError: reportPublishError,
      saveProjectStatus: isFinalStep,
    });
    if (!saved) return;

    if (isFinalStep) {
      dispatch(addMessage({ type: "info", message: "Настройки сохранены" }));
      return;
    }

    setSaveState("idle");
    setActiveStep(SETTINGS_STEPS[currentStepIndex + 1].id);
  };

  const handleAddPoster = (event, id) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      const base64String = reader.result.split(",")[1];
      dispatch(
        loadPoster({
          gameId: id,
          base64String,
          onSucces: () => dispatch(getGame({ gameId: game._id })),
        })
      );
    };
  };

  const handleShowExamplePost = () => {
    dispatch(
      addMessage({
        type: "help",
        message: "Пример стартового поста",
      })
    );
    dispatch(
      addMessage({
        type: "start-post",
        label: null,
        preview: EXAMPLE_START_POST,
      })
    );
  };

  const posterTitle = settings.title || "Название вашего сценария";

  const posterDescription =
    settings.description || DESCRIPTION_PLACEHOLDER;
  const activeStepMeta =
    SETTINGS_STEPS.find((step) => step.id === activeStep) ?? SETTINGS_STEPS[0];
  const stepIndex = SETTINGS_STEPS.findIndex((step) => step.id === activeStep);

  return (
    <div className="app-editor-settings">
      <div className="app-editor-settings__shell">
        <aside className="app-editor-settings__aside">
          <section className="app-settings-card app-settings-card--info">
            <div className="app-project-info__head">
              <h3 className="app-settings-card__title app-settings-card__title--left">
                О проекте
              </h3>
              <span className="app-settings-type-badge">
                {getContentTypeLabel(resolveGameContentType(game))}
              </span>
            </div>

            <dl className="app-project-info__list">
              <div className="app-project-info__row">
                <dt>Изменён</dt>
                <dd>{formatDateTimeRu(game?.updatedAt || game?.createdAt)}</dd>
              </div>
              <div className="app-project-info__row app-project-info__row--id">
                <dt>ID</dt>
                <dd className="app-settings-id">{game?._id}</dd>
              </div>
            </dl>
          </section>
        </aside>

        <div className="app-editor-settings__main">
          <nav className="app-settings-steps" aria-label="Шаги настройки">
            {SETTINGS_STEPS.map((step, index) => (
              <button
                key={step.id}
                type="button"
                className={`app-settings-steps__item${
                  step.id === activeStep ? " app-settings-steps__item--active" : ""
                }${index < stepIndex ? " app-settings-steps__item--done" : ""}`}
                onClick={() => setActiveStep(step.id)}
              >
                <span className="app-settings-steps__num">{index + 1}</span>
                <span className="app-settings-steps__label">{step.label}</span>
              </button>
            ))}
          </nav>

          <section className="app-settings-card app-settings-card--step">
            <header className="app-settings-step__head">
              <h3 className="app-settings-card__title app-settings-card__title--left">
                {activeStepMeta.label}
              </h3>
              {activeStepMeta.hint ? (
                <p className="app-settings-step__hint">{activeStepMeta.hint}</p>
              ) : null}
            </header>

            {activeStep === "start" ? (
              <div className="app-settings-poster-grid app-settings-poster-grid--step">
                <div className="app-settings-poster-fields">
                  <div className="app-settings-field">
                    <label className="app-settings-field__label" htmlFor="title">
                      Название
                    </label>
                    <TextField
                      onChange={(event) => handleChange(event, "title")}
                      id="title"
                      placeholder="Название вашего сценария"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={settings?.title ?? ""}
                      sx={fieldSx}
                    />
                  </div>

                  <div className="app-settings-field">
                    <label className="app-settings-field__label" htmlFor="program">
                      Программа / отдел
                    </label>
                    <TextField
                      onChange={(event) => handleChange(event, "program")}
                      id="program"
                      placeholder="Например: Онбординг, Отдел продаж"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={settings?.program ?? ""}
                      sx={fieldSx}
                    />
                  </div>

                  <div className="app-settings-field">
                    <label
                      className="app-settings-field__label"
                      htmlFor="description"
                    >
                      Текст стартового поста
                    </label>
                    <TextField
                      id="description"
                      placeholder={DESCRIPTION_PLACEHOLDER}
                      variant="outlined"
                      multiline
                      minRows={3}
                      fullWidth
                      value={settings?.description ?? ""}
                      onChange={(event) => handleChange(event, "description")}
                      sx={fieldSx}
                    />
                  </div>

                  <div className="app-settings-poster__actions">
                    <InputFileUpload
                      handleAddPoster={(event) => handleAddPoster(event, game._id)}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      className="app-btn app-btn--secondary app-btn--compact"
                      startIcon={<VisibilityOutlinedIcon />}
                      onClick={handleShowExamplePost}
                    >
                      Пример стартового поста
                    </Button>
                  </div>
                </div>

                <div className="app-settings-poster">
                  {data?.poster ? (
                    <Tooltip title="Удалить постер" placement="top">
                      <DeleteIcon
                        className="app-settings-poster__delete"
                        onClick={() => {
                          dispatch(
                            deletePoster({
                              gameId: game._id,
                              onSucces: () =>
                                dispatch(getGamesData({ id: user.id })),
                            })
                          );
                        }}
                      />
                    </Tooltip>
                  ) : null}

                  <div
                    className={`app-settings-poster__media${
                      data?.poster ? "" : " app-settings-poster__media--logo"
                    }`}
                  >
                    <img
                      src={
                        data?.poster
                          ? getServerMediaUrl(data.poster)
                          : SERVICE_LOGO_SRC
                      }
                      alt={data?.poster ? "Постер сценария" : "Логотип InChat"}
                    />
                  </div>

                  <div className="app-settings-poster__caption">
                    <p className="app-settings-poster__caption-title">
                      {posterTitle}
                    </p>
                    <p>{posterDescription}</p>
                  </div>
                </div>
              </div>
            ) : null}

            {activeStep === "publish" ? (
              <div className="app-settings-publish">
                <div className="app-settings-field app-settings-field--status">
                  <span className="app-settings-field__label">Статус проекта</span>
                  <RadioGroup
                    row
                    className="app-settings-status"
                    value={settings.projectStatus}
                    onChange={(event) => handleChange(event, "projectStatus")}
                  >
                    <FormControlLabel
                      value="published"
                      control={<Radio size="small" />}
                      label="Опубликовать"
                      className={`app-settings-status__option${
                        settings.projectStatus === "published"
                          ? " app-settings-status__option--selected"
                          : ""
                      }`}
                    />
                    <FormControlLabel
                      value="draft"
                      control={<Radio size="small" />}
                      label="В работе"
                      className={`app-settings-status__option${
                        settings.projectStatus === "draft"
                          ? " app-settings-status__option--selected"
                          : ""
                      }`}
                    />
                    <FormControlLabel
                      value="hidden"
                      control={<Radio size="small" />}
                      label="Остановить"
                      className={`app-settings-status__option${
                        settings.projectStatus === "hidden"
                          ? " app-settings-status__option--selected"
                          : ""
                      }`}
                    />
                  </RadioGroup>
                </div>
                <div className="app-settings-toggle">
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={settings.allow_replay}
                        onChange={handleToggle("allow_replay")}
                      />
                    }
                    label="Разрешить повторное прохождение"
                  />
                  <p className="app-settings-toggle__hint">
                    Если выключено, клиент не сможет пройти сценарий заново.
                  </p>
                </div>
              </div>
            ) : null}

            {activeStep === "gamification" ? (
              <GamificationSettings
                embedded
                value={settings.gamification}
                onChange={(gamification) =>
                  setSettings((prev) => ({ ...prev, gamification }))
                }
              />
            ) : null}

            {activeStep === "access" ? (
              <ProjectTeamAccess
                embedded
                gameId={game?._id}
                value={settings.availableMessengers}
                onChange={(next) =>
                  setSettings((prev) => ({
                    ...prev,
                    availableMessengers: next,
                  }))
                }
                chatAccessSettings={chatAccessGate}
                onChatAccessChange={setChatAccessGate}
                hintExtra={
                  <>
                    {" "}
                    <Link to="/main/price">Тарифы</Link>
                  </>
                }
              />
            ) : null}

            <footer className="app-settings-step__footer">
              <div className="app-settings-step__nav">
                {stepIndex > 0 ? (
                  <Button
                    size="small"
                    variant="outlined"
                    className="app-btn app-btn--secondary app-btn--compact"
                    onClick={() => setActiveStep(SETTINGS_STEPS[stepIndex - 1].id)}
                  >
                    Назад
                  </Button>
                ) : (
                  <span />
                )}
                <Button
                  size="small"
                  variant="contained"
                  className="app-btn app-btn--primary app-btn--compact"
                  disabled={saveState === "saving"}
                  onClick={handleNextStep}
                >
                  {saveState === "saving"
                    ? "Сохранение…"
                    : saveState === "saved"
                      ? "Сохранено"
                      : stepIndex < SETTINGS_STEPS.length - 1
                        ? "Дальше"
                        : "Готово"}
                </Button>
              </div>
            </footer>
          </section>
        </div>
      </div>
    </div>
  );
}
